import React, { useEffect, useState } from "react";
import dynamic from "next/dynamic";
import styled from "styled-components";

import type { FilterSelectItem } from "./FilterSelect";
import Button from "../styled/Button";
import { SvgBackground } from "../ui/SvgBackground";

const FilterSelect = dynamic(() => import("./FilterSelect"));
const MobileBreakPoints = ["base", "mobile"];

const Container = styled.div<{ isActive: boolean }>`
  display: inline-flex;
  background-color: ${({ isActive }) => (isActive ? "#000" : "#fff")};
  ${(props) =>
    props.theme.apply("default", (breakpoint: string) => {
      return `
          height: ${props.theme.spacePx(
            breakpoint,
            MobileBreakPoints.includes(breakpoint) ? 6 : 7
          )};
          border: 1px solid #000;
          
        `;
    })}
`;

const Sep = styled.div<{ isActive: boolean }>`
  width: 1px;
  height: 100%;
  background-color: ${({ isActive }) => (isActive ? "#fff" : "#000")};
`;

const Arrow = styled.div<{ isActive: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  color: ${({ isActive }) => (isActive ? "#fff" : "#000")};
  ${(props) =>
    props.theme.apply("default", (breakpoint: string) => {
      return `
          width: ${props.theme.spacePx(breakpoint, 6)};
          ${props.theme.textStyle(breakpoint, `h4Heading`)};
          font-weight: 400;
          & span {
            padding-top: ${(
              1 + props.theme.fontValign(breakpoint, "h4Heading")
            ).toFixed(2)}px;
          }
        `;
    })}
`;

const Square = styled.div`
  & span {
    filter: invert(100%);
  }
  ${(props) =>
    props.theme.apply("default", (breakpoint: string) => {
      return `
        width: ${props.theme.spacePx(breakpoint, 9)};
        height: ${props.theme.spacePx(breakpoint, 9)};
      `;
    })}
`;

const Cross = styled(Button)`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  background-color: #000;

  ${(props) =>
    props.theme.apply("default", (breakpoint: string) => {
      return `
          width: ${(-2 + props.theme.space(breakpoint, 7)).toFixed(2)}px;
          
        `;
    })}
`;

const MONTHS: FilterSelectItem[] = [...Array(12)].map(
  (_value: number, index: number) => {
    return {
      value: index,
      label: new Date(
        `2021-${index < 9 ? "0" : ""}${index + 1}-01T12:00:00`
      ).toLocaleString("en-GB", { month: "short" }),
    };
  }
);

const createDate = (date: string | undefined | null) => {
  if (!date) return null;

  try {
    const parsedDate = new Date(date);

    if (!isNaN(parsedDate.getTime())) {
      parsedDate.setDate(1);
      // we don't want to use midnight as this might spell trouble with time zone conversions.
      parsedDate.setHours(12, 0, 0, 0);
      return parsedDate;
    }
  } catch (err) {}

  return null;
};

export const FilterDateRange = ({
  firstMonth = 0,
  firstYear,
  showMonths = true,
  addUntilYears = 0,
  from,
  until,
  onRangeChange,
}: {
  showMonths?: boolean;
  firstMonth?: number;
  firstYear: number;
  addUntilYears?: number;
  from: string | undefined | null;
  until: string | undefined | null;
  onRangeChange: (dateFrom: Date | null, dateUntil: Date | null) => void;
}) => {
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth();

  const [dateFrom, setDateFrom] = useState<Date | null>(null);
  const [dateUntil, setDateUntil] = useState<Date | null>(null);
  const [isUserInteraction, setIsUserInteraction] = useState(false);

  useEffect(() => {
    const fallback = showMonths
      ? `${firstYear}-${firstMonth < 9 ? "0" : ""}${firstMonth + 1}`
      : `${firstYear}`;

    const date = createDate(from) ?? createDate(fallback);
    if (date) {
      date?.setDate(1);
      setDateFrom(date);
    }
  }, [from, firstYear, firstMonth, showMonths]);

  useEffect(() => {
    let yearFallback = new Date();
    yearFallback.setFullYear(yearFallback.getFullYear() + addUntilYears);
    yearFallback.setDate(1);
    const date = createDate(until) ?? yearFallback;
    if (date) {
      setDateUntil(date);
    }
  }, [until, addUntilYears]);

  const dateFromDate = dateFrom?.toISOString()?.split("T")?.[0];
  const dateUntilDate = dateUntil?.toISOString()?.split("T")?.[0];

  useEffect(() => {
    if (dateFromDate && dateUntilDate) {
      if (isUserInteraction && typeof onRangeChange === "function") {
        onRangeChange.call(
          null,
          new Date(dateFromDate),
          new Date(dateUntilDate)
        );
      }
    }

    setIsUserInteraction(false);
  }, [dateFromDate, dateUntilDate, onRangeChange, isUserInteraction]);

  const years: FilterSelectItem[] = [
    ...Array(currentYear + 1 + addUntilYears - firstYear),
  ].map((_value: number, index: number) => {
    const year = firstYear + index;
    return {
      value: year,
      label: `${year}`,
    };
  });

  const dateMaxUntil = new Date();
  dateMaxUntil.setFullYear(dateMaxUntil.getFullYear() + addUntilYears);
  dateMaxUntil.setDate(1);

  const showResetButton = !!(
    dateFrom &&
    dateUntil &&
    ((showMonths && dateFrom?.getMonth() !== firstMonth) ||
      dateFrom?.getFullYear() !== firstYear ||
      (showMonths && dateUntil?.getMonth() !== dateMaxUntil.getMonth()) ||
      dateUntil?.getFullYear() !== dateMaxUntil.getFullYear())
  );

  return (
    <Container isActive={showResetButton}>
      {showMonths && (
        <FilterSelect
          items={MONTHS}
          selected={dateFrom?.getMonth() ?? firstMonth}
          label="month from"
          labelSelect="Month"
          colorScheme={showResetButton ? "black" : "white"}
          onChange={(item: FilterSelectItem | null | undefined) => {
            if (item && dateFrom?.getTime() && dateUntil?.getTime()) {
              dateFrom.setMonth(parseInt(`${item.value}`));
              setDateFrom(new Date(dateFrom.getTime()));

              if (dateUntil < dateFrom) {
                setDateUntil(new Date(dateFrom.getTime()));
              }
              setIsUserInteraction(true);
            }
          }}
        />
      )}
      {showMonths && <Sep isActive={showResetButton} />}
      <FilterSelect
        items={years}
        selected={dateFrom?.getFullYear() ?? firstYear}
        label="year from"
        labelSelect="Year"
        colorScheme={showResetButton ? "black" : "white"}
        onChange={(item: FilterSelectItem | null | undefined) => {
          if (item && dateFrom?.getTime() && dateUntil?.getTime()) {
            dateFrom.setFullYear(parseInt(`${item.value}`));
            setDateFrom(new Date(dateFrom.getTime()));

            if (dateUntil < dateFrom) {
              setDateUntil(new Date(dateFrom.getTime()));
            }

            setIsUserInteraction(true);
          }
        }}
      />
      <Sep isActive={showResetButton} />
      <Arrow isActive={showResetButton}>
        <span>{"->"}</span>
      </Arrow>
      <Sep isActive={showResetButton} />
      {showMonths && (
        <FilterSelect
          items={MONTHS}
          selected={dateUntil?.getMonth() ?? currentMonth}
          label="month until"
          labelSelect="Month"
          colorScheme={showResetButton ? "black" : "white"}
          onChange={(item: FilterSelectItem | null | undefined) => {
            if (item && dateFrom?.getTime() && dateUntil?.getTime()) {
              dateUntil.setMonth(parseInt(`${item.value}`));

              setDateUntil(new Date(dateUntil.getTime()));

              if (dateUntil < dateFrom) {
                setDateFrom(new Date(dateUntil.getTime()));
              }
              setIsUserInteraction(true);
            }
          }}
        />
      )}
      {showMonths && <Sep isActive={showResetButton} />}
      <FilterSelect
        items={years}
        selected={dateUntil?.getFullYear() ?? currentYear}
        label="year until"
        labelSelect="Year"
        colorScheme={showResetButton ? "black" : "white"}
        onChange={(item: FilterSelectItem | null | undefined) => {
          if (item && dateFrom?.getTime() && dateUntil?.getTime()) {
            dateUntil.setFullYear(parseInt(`${item.value}`));
            setDateUntil(new Date(dateUntil.getTime()));

            if (dateUntil < dateFrom) {
              setDateFrom(new Date(dateUntil.getTime()));
            }
            setIsUserInteraction(true);
          }
        }}
      />
      {showResetButton && (
        <>
          <Sep isActive={showResetButton} />

          <Cross
            aria-label="Reset date range filter"
            onClick={(event) => {
              event.preventDefault();
              setDateFrom(null);
              setDateUntil(null);
              if (typeof onRangeChange === "function")
                onRangeChange.call(null, null, null);
            }}
          >
            {" "}
            <Square>
              <SvgBackground
                className="svg close"
                type="close"
                position="left center"
                width="100%"
                height="100%"
              />
            </Square>
          </Cross>
        </>
      )}
    </Container>
  );
};
